import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared, localStorage-persisted column layout for the wide data tables
 * (采购报价 page + 采购报价记录 dialog): drag-to-resize widths and an optional
 * hidden-column set.
 *
 * @param storageKey  localStorage key prefix (widths are stored under it)
 * @param defaults    optional default width map; when omitted the caller can
 *                    call ``measure(tableEl)`` to seed widths from the natural
 *                    (auto) layout so the look stays unchanged.
 */
export function useColumnLayout(storageKey, defaults = null) {
  const [widths, setWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || "{}");
      return defaults ? { ...defaults, ...saved } : saved;
    } catch {
      return defaults || {};
    }
  });
  const ref = useRef(widths);
  ref.current = widths;

  const persist = useCallback((next) => {
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* private mode / quota */ }
  }, [storageKey]);

  const setWidth = useCallback((key, w) => {
    const next = { ...ref.current, [key]: w };
    setWidths(next);
    persist(next);
  }, [persist]);

  const startResize = useCallback((key, minWidth = 48) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.closest?.("th");
    const startX = e.clientX;
    const startW = ref.current[key] || Math.round(th?.getBoundingClientRect().width || 120);
    const onMove = (ev) => {
      const w = Math.max(minWidth, Math.round(startW + (ev.clientX - startX)));
      setWidths((prev) => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      persist(ref.current);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [persist]);

  // Seed widths from the rendered header (auto layout) — used by tables that
  // have no hard-coded defaults.
  const measure = useCallback((tableEl) => {
    if (!tableEl) return false;
    const next = {};
    tableEl.querySelectorAll("th[data-col]").forEach((th) => {
      next[th.dataset.col] = Math.round(th.getBoundingClientRect().width);
    });
    if (Object.keys(next).length === 0) return false;
    setWidths((prev) => ({ ...next, ...prev }));  // saved widths win
    return true;
  }, []);

  const resetAll = useCallback(() => {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    setWidths(defaults ? { ...defaults } : {});
  }, [storageKey, defaults]);

  return { widths, setWidths, setWidth, startResize, measure, resetAll, persist };
}

/** Persisted hidden-column set (列显示开关). */
export function useHiddenColumns(storageKey) {
  const [hidden, setHidden] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(storageKey) || "[]")); } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(Array.from(hidden))); } catch { /* quota */ }
  }, [hidden, storageKey]);
  const toggle = useCallback((key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);
  const showAll = useCallback(() => setHidden(new Set()), []);
  return { hidden, toggle, showAll };
}
